<template>
  <ReviewBoosterContainer>
    <template #header>
      <ReviewBoosterHeader
        :tags="tags"
        :payload="payload"
        @list-update="fetchTags"
        @apply-filters="applyFilters"
      >
        <DatePicker v-if="date" :value="date" @input="handleDateChange" />
      </ReviewBoosterHeader>
    </template>
    <template #content>
      <SelectForm class="select-booster" />

      <ReviewBoosterPageControls
        :is-loading="isLoading"
        :total-count="totalCount"
        :page-params="pageParams"
        :default-order="payload.orderBy"
        class="mb-6"
        @page-change="handlePageChange"
        @order-by-change="handleOrderByChange"
      />
      <ReviewBoosterListContainer
        :reviews="reviews"
        :tags="tags"
        :is-loading="isLoading"
      />

      <ReviewBoosterPageControls
        :total-count="totalCount"
        :page-params="pageParams"
        simplified
        @page-change="handlePageChange"
      />

      <SectionLoader :status="isLoading" />
    </template>
  </ReviewBoosterContainer>
</template>

<script>
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { PERIOD } from "@/enums/period.enum";
import reviewBoosterService from "@/services/review-booster";
import SectionLoader from "@/components/Loader/SectionLoader";
import ReviewBoosterHeader from "@/components/ReviewBooster/ReviewBoosterHeader.vue";
import SelectForm from "@/components/ReviewBooster/SelectForm";
import ReviewBoosterPageControls from "@/components/ReviewBooster/ReviewBoosterPageControls.vue";
import serviceHandler from "@/helpers/handlers/service";
import pickBy from "lodash/pickBy";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import tagsService from "@/services/tags";
import mapValues from "lodash/mapValues";
import isEqual from "lodash/isEqual";
import DatePicker from "@/components/ReviewBooster/DatePicker.vue";
import ReviewBoosterContainer from "@/components/ReviewBooster/ReviewBoosterContainer.vue";
import ReviewBoosterListContainer from "@/components/ReviewBooster/ReviewBoosterListContainer.vue";
import _ from "lodash";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
const defaultParams = Object.freeze({
  page: 1,
  pageSize: 10
});

const defaultPayload = Object.freeze({
  orderBy: null,
  dateFrom: null,
  dateTo: null,
  reviewBoosterKey: null
});
export default {
  name: "ReviewBoosterReviewsList",
  components: {
    SectionLoader,
    ReviewBoosterPageControls,
    SelectForm,
    ReviewBoosterHeader,
    DatePicker,
    ReviewBoosterContainer,
    ReviewBoosterListContainer
  },
  provide: {
    isCategoriesEnabled: isFeatureFlagEnabled(FEATURE_FLAG.SHOW_RB_CATEGORIES),
    showReviewAddedToGoogleMaps: isFeatureFlagEnabled(
      FEATURE_FLAG.SHOW_REVIEW_ADDED_WITH_RB
    )
  },
  mixins: [permissionsInfo],
  data() {
    return {
      reviews: null,
      isLoading: false,
      date: null,
      totalCount: 0,
      payload: { ...defaultPayload },
      params: { ...defaultParams },
      filters: null,
      isExportLoading: false,
      formList: null,
      tags: null,
      isInitialLoad: true,
      pickedFilters: null
    };
  },
  computed: {
    componentType() {
      const types = ["groups", "gmbAddressIds", "cities"];

      for (const type of types) {
        if (this.payload[type] && this.payload[type].length) {
          return type;
        }
      }

      return "";
    },
    pageParams() {
      return {
        page: this.params.page,
        pageSize: this.params.pageSize
      };
    },

    defaultStartDate() {
      return [
        moment()
          .subtract(1, PERIOD.MONTH)
          .add(1, PERIOD.DAY)
          .format(FORMAT.DATE),
        moment().format(FORMAT.DATE)
      ];
    }
  },

  methods: {
    async fetchTags() {
      if (!this.canTagReviews) {
        return;
      }
      this.tags = await serviceHandler(tagsService.getTags());
    },

    setDefaultPayload() {
      this.setDefaultDate();
      if (this.$route.query.orderBy) {
        this.payload.orderBy = this.$route.query.orderBy;
      } else {
        this.payload.orderBy = "-created_at";
      }

      this.setDefaultPaging();

      this.payload = {
        ...this.$route.query,
        ...this.payload,
        ...mapValues(this.params, String),
        orderBy: this.$route.query.orderBy || "-created_at",
        reviewBoosterKey:
          this.$route.query.reviewBoosterKey || this.formList[0].key
      };
    },
    setDefaultPaging() {
      if (!this.$route.query.page || !this.$route.query.pageSize) {
        this.params = defaultParams;
      } else {
        this.params = {
          page: +this.$route.query.page,
          pageSize: +this.$route.query.pageSize
        };
      }
    },

    setDefaultDate() {
      if (this.$route.query.dateFrom && this.$route.query.dateTo) {
        this.date = [this.$route.query.dateFrom, this.$route.query.dateTo];
      } else {
        this.date = this.defaultStartDate;
      }
      this.setDatePayload(this.date);
    },
    setDatePayload(date) {
      const [dateFrom, dateTo] = date;
      if (dateFrom && dateTo) {
        this.payload.dateFrom = dateFrom;
        this.payload.dateTo = dateTo;
      }
    },

    async handleDateChange(date) {
      this.date = date;
      const [dateFrom, dateTo] = date;
      this.setDatePayload(date);

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.FILTER_DATE_PERIOD,
        date_from: dateFrom,
        date_to: dateTo
      });
      this.$router.push({
        query: {
          ...this.$route.query,
          dateFrom: dateFrom,
          dateTo: dateTo
        }
      });
    },
    getPreparedQueryParam(query) {
      return Array.isArray(query) ? query.join(",") : query;
    },

    async updateRouteQuery(newQuery) {
      const requiredParams = this.getOnlyRequiredParamsFromQuery();
      const query = { ...requiredParams, ...newQuery };
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) {
        return;
      }
      await this.$router.push({ query });
    },
    getOnlyRequiredParamsFromQuery() {
      const requiredParams = { ...defaultParams, ...defaultPayload };
      return Object.keys(this.$route.query)
        .filter(key => key in requiredParams)
        .reduce((obj, key) => {
          obj[key] = this.$route.query[key];
          return obj;
        }, {});
    },
    transformFiltersToQuery(filterPayload) {
      return Object.entries(filterPayload).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value.toString();
        }
        return acc;
      }, {});
    },
    async applyFilters(filterPayload) {
      const filtersTransformed = this.transformFiltersToQuery(filterPayload);
      this.pickedFilters = pickBy(filtersTransformed);
      this.payload = { ...this.payload, ...filterPayload };
      this.params = defaultParams;
      this.updateRouteQuery({ ...this.pickedFilters, ...this.params });
    },
    getPagingAction(paging) {
      return paging.page !== this.params.page &&
        paging.pageSize === this.params.pageSize
        ? "page_num_changed"
        : "page_size_changed";
    },
    async handlePageChange(paging) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LIST_PAGING,
        action: this.getPagingAction(paging),
        component: this.componentType
      });
      this.params = paging;
      this.updateRouteQuery({ ...this.pickedFilters, ...this.params });
    },
    async handleOrderByChange(orderBy) {
      this.payload.orderBy = orderBy;
      this.params = defaultParams;
      this.updateRouteQuery({ ...this.pickedFilters, ...this.params, orderBy });
    },

    async fetchReviews() {
      const {
        data: { results, count }
      } = await reviewBoosterService.getReviews(this.payload, this.params);
      this.reviews = results;
      this.totalCount = count;
    },

    isReviewBoosterKeyChanged(reviewBoosterKey, last) {
      return last && reviewBoosterKey !== last.query.reviewBoosterKey;
    },

    updatePayload(reviewBoosterKey) {
      this.payload.reviewBoosterKey = reviewBoosterKey;
    },
    async getReviewBoosterList() {
      const { results } = await serviceHandler(
        reviewBoosterService.getReviewBoosterList()
      );
      this.formList = results;
    }
  },
  watch: {
    "$route.query": {
      async handler(current, last) {
        if (!Object.keys(current).length) return;
        this.isLoading = true;
        if (!last) {
          await this.getReviewBoosterList();
        }
        this.setDefaultPayload();
        await this.fetchReviews();
        this.isLoading = false;
        await this.fetchTags();
        if (!last) {
          if (!isEqual(this.$route.query, this.payload)) {
            await this.$router.replace({
              query: this.payload
            });
          }
        }
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.select-booster {
  margin-bottom: $spacing * 2.25;
}
</style>
